/**
 * Shared RAG (Red / Amber / Green) status helpers.
 *
 * Keeps the color mapping consistent across the dashboard and track detail
 * views. Uses the validated BOI RAG palette — the one place non-blue color is
 * allowed, since functional status is the brand's data/chart exception. Every
 * chip carries its text label; color is never the only signal.
 *
 * The generated API types expose `rag` as a plain `string`, so the helpers
 * accept `string` and normalize; `Rag` is the constrained UI type used by the
 * status editor's fixed set of options.
 */

export type Rag = "green" | "amber" | "red";

/** Coerce an API `rag` string into the constrained UI union. */
export function normalizeRag(value: string | null | undefined): Rag {
  return value === "amber" || value === "red" ? value : "green";
}

/**
 * Tailwind classes for a solid, flat RAG chip. Amber is light, so it takes dark
 * text for contrast; green and red take white text.
 */
export function ragBadgeClass(rag: string): string {
  const base = "rounded-sm px-2 py-0.5 text-xs font-medium uppercase";
  switch (rag) {
    case "green":
      return `${base} bg-rag-green text-[#0D1314]`;
    case "amber":
      return `${base} bg-rag-amber text-[#0D1314]`;
    case "red":
      return `${base} bg-rag-red text-[#0D1314]`;
    default:
      return `${base} bg-muted text-foreground`;
  }
}

/** Human-readable label for a RAG value. */
export function ragLabel(rag: string): string {
  switch (rag) {
    case "green":
      return "On track";
    case "amber":
      return "At risk";
    case "red":
      return "Off track";
    default:
      return rag;
  }
}
